import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  BarChart3, TrendingUp, IndianRupee, Users,
  ShoppingCart, Calendar, Download, Target,
  UserPlus, UserMinus, Info
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  AreaChart, Area
} from "recharts";
import { superAdminAPI } from "../../api/apiService";
import { toast } from "react-hot-toast";

export default function GlobalReports() {
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState("month");
  const [revenueData, setRevenueData] = useState([]);
  const [planUsageData, setPlanUsageData] = useState([]);
  const [stats, setStats] = useState({
    monthly_revenue: 0,
    total_revenue: 0,
    total_owners: 0,
    active_owners: 0,
    trial_users: 0,
    expired_users: 0,
    conversion_rate: 0,
    failed_payments: 0
  });

  useEffect(() => {
    fetchAllData();
  }, [dateRange]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      // 1. Fetch Stats
      const statsRes = await superAdminAPI.getDashboardStats();
      setStats(statsRes.data);

      // 2. Fetch Revenue Trend
      const revenueTrendRes = await superAdminAPI.getGlobalReports('revenue');
      setRevenueData(revenueTrendRes.data.data);

      // 3. Fetch Plan Usage
      const plansRes = await superAdminAPI.getGlobalReports('plans');
      setPlanUsageData(plansRes.data.data);

    } catch (error) {
      console.error("Error fetching report data:", error);
      toast.error("Failed to load business analytics");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatCurrencyWithDecimals = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white dark:bg-slate-800 p-4 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl backdrop-blur-md bg-opacity-95">
          <p className="text-xs font-black text-slate-400 uppercase tracking-widest mb-2">{label}</p>
          {payload.map((entry, index) => (
            <div key={index} className="flex justify-between items-center gap-8 py-1">
              <span className="text-xs text-slate-500 dark:text-slate-400 font-bold">{entry.name}:</span>
              <span className="text-sm font-black text-slate-900 dark:text-white">
                {entry.name.toLowerCase().includes('revenue') || entry.name.toLowerCase().includes('price')
                  ? formatCurrencyWithDecimals(entry.value)
                  : entry.value}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-8"
    >
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight">Platform Insights</h2>
          <p className="text-slate-500 dark:text-slate-400 font-medium">Strategic business analytics for Super Admin</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Calendar size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="pl-10 pr-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm font-bold text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-slate-900 dark:focus:ring-white transition-all appearance-none cursor-pointer"
            >
              <option value="week">Week to Date</option>
              <option value="month">Month to Date</option>
              <option value="year">Year to Date</option>
            </select>
          </div>
          <button className="p-2.5 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-xl hover:opacity-90 transition-opacity">
            <Download size={20} />
          </button>
        </div>
      </div>

      {/* Primary Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Revenue Card */}
        <motion.div whileHover={{ y: -4 }} className="p-6 bg-white dark:bg-slate-800 rounded-3xl border border-slate-200 dark:border-slate-700 shadow-xl relative overflow-hidden group">
          <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/5 rounded-full -mr-16 -mt-16 group-hover:scale-110 transition-transform duration-500" />
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-emerald-500/10 rounded-2xl">
              <IndianRupee className="text-emerald-500" size={24} />
            </div>
            <span className="text-[10px] font-black text-emerald-500 bg-emerald-500/10 px-2 py-1 rounded-lg uppercase">This Month</span>
          </div>
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Monthly Revenue</p>
          <h3 className="text-3xl font-black text-slate-900 dark:text-white mt-1">{formatCurrency(stats.monthly_revenue)}</h3>
          <div className="mt-4 flex items-center gap-2 text-[10px] font-bold text-slate-500">
            <span>Overall: {formatCurrency(stats.total_revenue)}</span>
          </div>
        </motion.div>

        {/* Owners Card */}
        <motion.div whileHover={{ y: -4 }} className="p-6 bg-white dark:bg-slate-800 rounded-3xl border border-slate-200 dark:border-slate-700 shadow-xl relative overflow-hidden group">
          <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/5 rounded-full -mr-16 -mt-16 group-hover:scale-110 transition-transform duration-500" />
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-blue-500/10 rounded-2xl">
              <Users className="text-blue-500" size={24} />
            </div>
            <div className="flex items-center gap-1.5 px-2 py-1 bg-blue-50 dark:bg-blue-500/10 rounded-lg">
              <div className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
              <span className="text-[10px] font-black text-blue-600 dark:text-blue-400 uppercase">{stats.active_owners} Active</span>
            </div>
          </div>
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Total Owners</p>
          <h3 className="text-3xl font-black text-slate-900 dark:text-white mt-1">{stats.total_owners}</h3>
          <div className="mt-4 flex items-center gap-4 text-[10px] font-bold text-slate-500">
            <span className="flex items-center gap-1"><UserPlus size={10} className="text-emerald-500" /> {stats.trial_users} Trial</span>
            <span className="flex items-center gap-1"><UserMinus size={10} className="text-red-500" /> {stats.expired_users} Expired</span>
          </div>
        </motion.div>

        {/* Conversion Card */}
        <motion.div whileHover={{ y: -4 }} className="p-6 bg-white dark:bg-slate-800 rounded-3xl border border-slate-200 dark:border-slate-700 shadow-xl relative overflow-hidden group">
          <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/5 rounded-full -mr-16 -mt-16 group-hover:scale-110 transition-transform duration-500" />
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-indigo-500/10 rounded-2xl">
              <Target className="text-indigo-500" size={24} />
            </div>
            <div className="p-1 px-2 bg-indigo-500/10 rounded-lg">
              <TrendingUp size={14} className="text-indigo-500" />
            </div>
          </div>
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Conversion Rate</p>
          <h3 className="text-3xl font-black text-slate-900 dark:text-white mt-1">{stats.conversion_rate}%</h3>
          <div className="mt-4 flex items-center gap-2 text-[10px] font-bold text-slate-400 italic">
            <Info size={10} />
            <span>Owners who upgraded to Paid</span>
          </div>
        </motion.div>

        {/* Failed Payments Card */}
        <motion.div whileHover={{ y: -4 }} className="p-6 bg-white dark:bg-slate-800 rounded-3xl border border-slate-200 dark:border-slate-700 shadow-xl relative overflow-hidden group">
          <div className="absolute top-0 right-0 w-32 h-32 bg-red-500/5 rounded-full -mr-16 -mt-16 group-hover:scale-110 transition-transform duration-500" />
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-red-500/10 rounded-2xl">
              <ShoppingCart className="text-red-500" size={24} />
            </div>
            {stats.failed_payments > 0 && (
              <span className="text-[10px] font-black text-red-500 bg-red-500/10 px-2 py-1 rounded-lg uppercase">Attention</span>
            )}
          </div>
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Failed Payments</p>
          <h3 className="text-3xl font-black text-slate-900 dark:text-white mt-1">{stats.failed_payments}</h3>
          <div className="mt-4 flex items-center gap-2 text-[10px] font-bold text-slate-500">
            <span>Pending recovery attempts</span>
          </div>
        </motion.div>
      </div>

      {/* Charts Layer */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Revenue Performance Area Chart */}
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          className="p-8 bg-white dark:bg-slate-800/40 rounded-[32px] border border-slate-200 dark:border-slate-700 shadow-2xl backdrop-blur-xl"
        >
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-500/10 rounded-2xl text-purple-500">
                <BarChart3 size={24} />
              </div>
              <div>
                <h3 className="text-xl font-black text-slate-900 dark:text-white">Revenue Momentum</h3>
                <p className="text-xs font-bold text-slate-500 uppercase tracking-widest">Growth over time</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Total ARR</p>
              <p className="text-lg font-black text-slate-900 dark:text-white">₹{(stats.monthly_revenue * 12).toLocaleString()}</p>
            </div>
          </div>

          <div className="h-[350px] w-full mt-4">
            {loading ? (
              <div className="h-full w-full flex items-center justify-center">
                <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-900 dark:border-slate-700 dark:border-t-white rounded-full animate-spin" />
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={revenueData}>
                  <defs>
                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(203, 213, 225, 0.15)" />
                  <XAxis
                    dataKey="name"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#94a3b8', fontSize: 10, fontWeight: 700 }}
                    dy={10}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#94a3b8', fontSize: 10, fontWeight: 700 }}
                    tickFormatter={(value) => `₹${value >= 1000 ? (value / 1000).toFixed(1) + 'k' : value}`}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="revenue"
                    stroke="#8B5CF6"
                    strokeWidth={4}
                    fillOpacity={1}
                    fill="url(#colorRevenue)"
                    animationDuration={1500}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </motion.div>

        {/* Subscription Plan Usage Chart */}
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          className="p-8 bg-white dark:bg-slate-800/40 rounded-[32px] border border-slate-200 dark:border-slate-700 shadow-2xl backdrop-blur-xl"
        >
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-emerald-500/10 rounded-2xl text-emerald-500">
                <Target size={24} />
              </div>
              <div>
                <h3 className="text-xl font-black text-slate-900 dark:text-white">Plan Performance</h3>
                <p className="text-xs font-bold text-slate-500 uppercase tracking-widest">Usage vs Profitability</p>
              </div>
            </div>
          </div>

          <div className="h-[350px] w-full mt-4">
            {loading ? (
              <div className="h-full w-full flex items-center justify-center">
                <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-900 dark:border-slate-700 dark:border-t-white rounded-full animate-spin" />
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={planUsageData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(203, 213, 225, 0.15)" />
                  <XAxis
                    dataKey="name"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#94a3b8', fontSize: 10, fontWeight: 700 }}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#94a3b8', fontSize: 10, fontWeight: 700 }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ paddingTop: 20, fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em' }} />
                  <Bar
                    dataKey="subscribers"
                    name="Subscribers"
                    fill="#3B82F6"
                    radius={[6, 6, 0, 0]}
                    barSize={32}
                    animationDuration={1500}
                  />
                  <Bar
                    dataKey="revenue"
                    name="Revenue"
                    fill="#10B981"
                    radius={[6, 6, 0, 0]}
                    barSize={32}
                    animationDuration={2000}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}
